import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toggle, PrimaryButton, ErrorBanner, Skeleton, ConfirmModal } from '@/components';
import { ContactHeader } from './ContactHeader';
import { ContactInfoSection, type ContactFormData } from './ContactInfoSection';
import { SendInviteSection } from './SendInviteSection';
import {
  useContactDetails,
  useUpdateContact,
  useToggleContactEnabled,
  useDeleteContact,
  useSendInviteLink,
} from '../../hooks';
import { ContactUpdateResponse } from '../../api/contacts';
import { SidebarContent, SidebarFooter } from '@/components/layout/sidepanel/SidebarFooter';

interface ContactEditPanelProps {
  companyId: string;
  contactId: string;
  onClose?: () => void;
}

export function ContactEditPanel({ companyId, contactId, onClose }: ContactEditPanelProps) {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    isMainContact: false,
    username: '',
  });

  const { data: contact, isLoading, isError } = useContactDetails(companyId, contactId);
  const updateContact = useUpdateContact(companyId, contactId);
  const toggleEnabled = useToggleContactEnabled(companyId, contactId);
  const deleteContactMutation = useDeleteContact(companyId, contactId);
  const sendInvite = useSendInviteLink(companyId);

  useEffect(() => {
    if (contact) {
      setFormData({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phoneNumber: contact.phoneNumber || '',
        isMainContact: contact.isMainContact,
        username: contact.user?.username || '',
      });
    }
  }, [contact]);

  const handleFieldChange = useCallback(
    <K extends keyof ContactFormData>(field: K, value: ContactFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setError(null);
    },
    []
  );

  const handleToggleEnabled = useCallback(
    async (checked: boolean) => {
      try {
        await toggleEnabled.mutateAsync(checked);
      } catch {
        setError(t('contact.errors.toggleFailed'));
      }
    },
    [toggleEnabled, t]
  );

  const handleMainContactChange = useCallback(
    async (checked: boolean) => {
      try {
        await updateContact.mutateAsync({ isMainContact: checked });
      } catch {
        setError(t('contact.errors.updateFailed'));
      }
    },
    [updateContact, t]
  );

  const handleSave = useCallback(async () => {
    if (!contact) return;

    setError(null);

    const isManagedUser = contact.user?.isManagedByCompany ?? false;

    const updateData: Record<string, unknown> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber || undefined,
      isMainContact: formData.isMainContact,
      userRoleID: formData.roleId,
    };

    if (isManagedUser) {
      updateData.username = formData.username;
    }

    try {
      const response = await updateContact.mutateAsync(updateData);

      if (response === ContactUpdateResponse.OK) {
        setIsEditMode(false);
      } else if (response === ContactUpdateResponse.EMAIL_EXISTS) {
        setError(t('contact.errors.emailExists'));
      } else {
        setError(t('contact.errors.updateFailed'));
      }
    } catch {
      setError(t('contact.errors.updateFailed'));
    }
  }, [contact, formData, updateContact, t]);

  const handleEditClick = useCallback(() => {
    setIsEditMode(true);
    setError(null);
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (contact) {
      setFormData({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phoneNumber: contact.phoneNumber || '',
        isMainContact: contact.isMainContact,
        username: contact.user?.username || '',
      });
    }
    setIsEditMode(false);
    setError(null);
  }, [contact]);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await deleteContactMutation.mutateAsync();
      setIsDeleteModalOpen(false);
      onClose?.();
    } catch {
      setError(t('contact.errors.deleteFailed'));
      setIsDeleteModalOpen(false);
    }
  }, [deleteContactMutation, onClose, t]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleSendInvite = useCallback(
    (email: string) => {
      sendInvite.mutate(
        { contactId, email },
        {
          onError: () => {
            setError(t('contact.invite.error'));
          },
        }
      );
    },
    [contactId, sendInvite, t]
  );

  const showInviteSection = !contact?.user && !contact?.invite;

  const panelWidth = 'sm:w-[40vw] sm:min-w-[500px]';

  if (isLoading) {
    return (
      <SidebarContent className={`space-y-4 p-4 ${panelWidth}`}>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </SidebarContent>
    );
  }

  if (isError || !contact) {
    return (
      <SidebarContent className={`p-4 ${panelWidth}`}>
        <ErrorBanner hasError={true}>{t('contact.errors.loadFailed')}</ErrorBanner>
      </SidebarContent>
    );
  }

  return (<>
    <SidebarContent className={`text-onsurface`}>
      <div className="flex items-center justify-end py-2">
        <span className="text-sm opacity-50 mx-2">{t('contact.enabled')}</span>
        <Toggle
          checked={contact.isEnabled}
          onChange={handleToggleEnabled}
          ariaLabel={t('contact.toggleEnabled')}
          disabled={toggleEnabled.isPending}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <ContactHeader
          firstName={contact.firstName}
          lastName={contact.lastName}
          createdAt={contact.createdAt}
          isManagedByCompany={contact.user?.isManagedByCompany ?? false}
        />

        <div className="my-8" />

        {error && (
          <div className="mb-4">
            <ErrorBanner hasError={true}>{error}</ErrorBanner>
          </div>
        )}

        <ContactInfoSection
          contact={contact}
          formData={formData}
          isEditMode={isEditMode}
          companyId={companyId}
          onFieldChange={handleFieldChange}
          onMainContactChange={handleMainContactChange}
        />

        {showInviteSection && (
          <div className="mt-6">
            <SendInviteSection
              onSendInvite={handleSendInvite}
              isLoading={sendInvite.isPending}
              defaultEmail={contact.email}
            />
          </div>
        )}
      </div>
    </SidebarContent>
    {contact.canEdit && (<SidebarFooter className='flex justify-between'>

      <button
        type="button"
        onClick={handleDeleteClick}
        className="px-4 py-2 text-sm font-medium text-error hover:text-error/80 transition-colors"
      >
        {t('contact.deleteContact')}
      </button>

      {isEditMode ? (
        <>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-4 py-2 text-sm font-medium opacity-50 hover:text-onsurface transition-colors"
            >
              {t('common.cancel')}
            </button>
            <PrimaryButton
              onClick={handleSave}
              disabled={updateContact.isPending}
            >
              {updateContact.isPending ? t('common.saving') : t('common.save')}
            </PrimaryButton>
          </div>
        </>
      ) : <><PrimaryButton onClick={handleEditClick}>
        {t('contact.editEmployee')}
      </PrimaryButton></>}

    </SidebarFooter>
    )}

    <ConfirmModal
      isOpen={isDeleteModalOpen}
      title={t('contact.deleteModal.title')}
      message={t('contact.deleteModal.message')}
      warningMessage={contact?.user?.isManagedByCompany ? t('contact.deleteModal.managedUserWarning') : undefined}
      confirmLabel={t('contact.deleteModal.confirm')}
      cancelLabel={t('contact.deleteModal.cancel')}
      onConfirm={handleDeleteConfirm}
      onCancel={handleDeleteCancel}
      isLoading={deleteContactMutation.isPending}
      variant="danger"
    />

  </>
  );
}
